import { LEXWARE_ARCHIVE_CHUNK_BYTES, type LexwareArchiveUpload } from '@erp/shared';
import { ApiError } from '@/lib/api-client';
import {
  appendLexwareArchiveChunk,
  getLexwareArchiveUpload,
} from '../api/use-lexware-archive-imports';

export class LexwareArchiveFileMismatch extends Error {}

const TRANSIENT_BACKOFF_MS = 300;
// The API's rate limiter uses a one-minute window, including for very fast ZIP uploads.
const RATE_LIMIT_BACKOFF_MS = 60_000;

function readChunk(chunk: Blob, signal: AbortSignal): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const abort = () => reader.abort();
    const cleanup = () => signal.removeEventListener('abort', abort);
    reader.onload = () => {
      cleanup();
      if (reader.result instanceof ArrayBuffer) resolve(reader.result);
      else reject(new Error('The ZIP chunk could not be read.'));
    };
    reader.onerror = () => {
      cleanup();
      reject(reader.error ?? new Error('The ZIP chunk could not be read.'));
    };
    reader.onabort = () => {
      cleanup();
      reject(new DOMException('Upload paused.', 'AbortError'));
    };
    signal.addEventListener('abort', abort, { once: true });
    if (signal.aborted) {
      cleanup();
      reject(new DOMException('Upload paused.', 'AbortError'));
      return;
    }
    reader.readAsArrayBuffer(chunk);
  });
}

const hex = (bytes: ArrayBuffer) =>
  Array.from(new Uint8Array(bytes), (byte) => byte.toString(16).padStart(2, '0')).join('');

/** Samples both ends even when they overlap, matching the server's resume identity contract. */
export async function lexwareArchiveFingerprint(file: File, signal: AbortSignal): Promise<string> {
  const first = await readChunk(file.slice(0, LEXWARE_ARCHIVE_CHUNK_BYTES), signal);
  const last = await readChunk(
    file.slice(Math.max(0, file.size - LEXWARE_ARCHIVE_CHUNK_BYTES)),
    signal,
  );
  const sample = new Uint8Array(first.byteLength + last.byteLength);
  sample.set(new Uint8Array(first));
  sample.set(new Uint8Array(last), first.byteLength);
  return hex(await crypto.subtle.digest('SHA-256', sample));
}

function delay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer);
      signal.removeEventListener('abort', done);
      resolve();
    };
    const timer = setTimeout(done, ms);
    signal.addEventListener('abort', done, { once: true });
    if (signal.aborted) done();
  });
}
const retryable = (error: unknown) =>
  error instanceof ApiError &&
  (error.status === 0 || error.status >= 500 || [408, 409, 429].includes(error.status));

/** Bounded slices and retries; a lost response is reconciled against the accepted server offset. */
export async function uploadLexwareArchiveChunks(
  file: File,
  fileFingerprint: string,
  id: string,
  signal: AbortSignal,
  onProgress: (upload: LexwareArchiveUpload) => void,
): Promise<LexwareArchiveUpload> {
  let upload = await getLexwareArchiveUpload(id, signal);
  onProgress(upload);
  if (upload.status !== 'UPLOADING') return upload;
  if (
    upload.fileName !== file.name ||
    upload.fileSize !== file.size ||
    upload.fileFingerprint !== fileFingerprint
  )
    throw new LexwareArchiveFileMismatch();
  try {
    while (upload.receivedBytes < file.size && !signal.aborted) {
      const offset = upload.receivedBytes;
      const chunk = file.slice(offset, Math.min(file.size, offset + LEXWARE_ARCHIVE_CHUNK_BYTES));
      const bytes = await readChunk(chunk, signal);
      const sha256 = hex(await crypto.subtle.digest('SHA-256', bytes));
      let next: LexwareArchiveUpload | undefined;
      for (let attempt = 0; attempt < 3 && !signal.aborted; attempt++) {
        try {
          next = await appendLexwareArchiveChunk(id, offset, sha256, chunk, signal);
          break;
        } catch (error) {
          if (signal.aborted) break;
          // The server may have accepted the chunk before the connection was interrupted.
          let accepted: LexwareArchiveUpload | undefined;
          try {
            accepted = await getLexwareArchiveUpload(id, signal);
          } catch {
            /* Retry remains bounded. */
          }
          if (accepted?.receivedBytes === offset + chunk.size) {
            next = accepted;
            break;
          }
          if (accepted !== undefined && accepted.receivedBytes !== offset)
            throw new Error('The accepted ZIP offset changed unexpectedly.');
          if (!retryable(error) || attempt === 2) throw error;
          await delay(
            error instanceof ApiError && error.status === 429
              ? RATE_LIMIT_BACKOFF_MS
              : TRANSIENT_BACKOFF_MS * (attempt + 1),
            signal,
          );
        }
      }
      if (signal.aborted && next === undefined) break;
      if (
        next === undefined ||
        next.receivedBytes !== offset + chunk.size ||
        next.receivedBytes > file.size
      )
        throw new Error('The accepted ZIP offset changed unexpectedly.');
      upload = next;
      onProgress(upload);
      if (upload.status !== 'UPLOADING') break;
    }
  } catch (error) {
    if (!signal.aborted) throw error;
  }
  return upload;
}
